import { useState } from "react";
import Header from "./app-header/Header";
import { API_ENDPOINTS } from "../constants/api";

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatDateShort(isoStr) {
  if (!isoStr) return "—";
  return new Date(isoStr).toLocaleDateString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const TABLE_HEADER = {
  background: "#f4f6f8", fontWeight: 600, fontSize: "0.78rem",
  color: "#374151", padding: "10px 14px", textAlign: "left",
  whiteSpace: "nowrap", borderBottom: "1px solid #e5e7eb",
};

const TABLE_CELL = {
  padding: "10px 14px", fontSize: "0.82rem", color: "#141414",
  borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap",
};

export default function DashboardScreen({
  userName,
  userPhotoUrl,
  submissionHistory = [],
  onNewSubmission,
  language,
  onLanguageChange,
  strings,
  verifyOnConfirm = true,
  onVerify,
  verificationResults = {},
  getToken,
}) {
  const initials = userName
    ? userName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "";

  const [expandedCaseId, setExpandedCaseId] = useState(null);
  const [caseDetailsCache, setCaseDetailsCache] = useState({});
  const [verifyingCaseId, setVerifyingCaseId] = useState(null);
  const [openExtractedDropdowns, setOpenExtractedDropdowns] = useState({});

  const toggleExtracted = (key) =>
    setOpenExtractedDropdowns((prev) => ({ ...prev, [key]: !prev[key] }));

  const formatLabel = (key) => {
    if (!key) return "";
    if (key === "payee_account_no") return "Payee Acc No";
    return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const loadCaseDetails = async (caseId) => {
    if (caseDetailsCache[caseId]?.status === "done" || caseDetailsCache[caseId]?.status === "loading" || caseDetailsCache[caseId]?.status === "error") return;
    setCaseDetailsCache((prev) => ({ ...prev, [caseId]: { status: "loading", shipments: [], invoices: [], tasks: [], validation_results: [] } }));
    try {
      const token = await getToken();
      const entry = submissionHistory.find((e) => e.caseId === caseId) || {};
      const params = new URLSearchParams();
      if (entry.bl_number) params.set("bl_number", entry.bl_number);
      if (entry.invoice_number) params.set("invoice_number", entry.invoice_number);
      const url = `${API_ENDPOINTS.caseDetails(caseId)}${params.toString() ? `?${params}` : ""}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      const shipments = data.shipments || [];
      const invoices = data.invoices || [];
      const tasks = data.tasks || [];
      const validation_results = data.validation_results || [];
      const isEmpty = !shipments.length && !invoices.length && !tasks.length && !validation_results.length;
      setCaseDetailsCache((prev) => ({
        ...prev,
        [caseId]: { status: isEmpty ? "empty" : "done", shipments, invoices, tasks, validation_results },
      }));
    } catch {
      setCaseDetailsCache((prev) => ({ ...prev, [caseId]: { status: "error", shipments: [], invoices: [], tasks: [], validation_results: [] } }));
    }
  };

  const handleToggleExpand = (caseId) => {
    if (expandedCaseId === caseId) {
      setExpandedCaseId(null);
    } else {
      setExpandedCaseId(caseId);
      loadCaseDetails(caseId);
    }
  };

  const handleVerify = async (entry) => {
    setVerifyingCaseId(entry.caseId);
    await onVerify(entry);
    setVerifyingCaseId(null);
  };

  const verifyPill = (result) => {
    if (!result) return null;
    const cfg = {
      COMPLIANT:         { bg: "#d1fae5", color: "#065f46", label: "Compliant" },
      "NON-COMPLIANT":   { bg: "#fee2e2", color: "#991b1b", label: "Non-Compliant" },
      EXCEPTION:         { bg: "#fef3c7", color: "#92400e", label: "Exception" },
      verified:          { bg: "#d1fae5", color: "#065f46", label: "Verified" },
      discrepancy_found: { bg: "#fef3c7", color: "#92400e", label: "Discrepancy Found" },
      unverifiable:      { bg: "#fee2e2", color: "#991b1b", label: "Unverifiable" },
    }[result.status] || { bg: "#f3f4f6", color: "#374151", label: result.status };
    return (
      <span style={{
        background: cfg.bg, color: cfg.color,
        borderRadius: "20px", padding: "3px 10px",
        fontSize: "0.78rem", fontWeight: 500,
      }}>
        {cfg.label}
      </span>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6f8" }}>
      <Header
        language={language}
        onLanguageChange={onLanguageChange}
        strings={strings}
        userName={userName}
        userPhotoUrl={userPhotoUrl}
      />

      <div style={{ paddingTop: "80px", maxWidth: "1100px", margin: "0 auto", padding: "80px 24px 40px" }}>

        {/* User Info Card */}
        <div style={{
          background: "#fff", borderRadius: "12px", padding: "24px 28px",
          boxShadow: "0 2px 12px rgba(0,0,0,0.07)", display: "flex",
          alignItems: "center", gap: "20px", marginBottom: "28px",
        }}>
          <div style={{
            width: "56px", height: "56px", borderRadius: "50%",
            background: "#00243d", color: "#fff", display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: "1.2rem", fontWeight: 700, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "#141414" }}>{userName}</div>
            <div style={{ fontSize: "0.85rem", color: "#6b7280", marginTop: "2px" }}>Maersk</div>
          </div>
          <button
            onClick={onNewSubmission}
            style={{
              marginLeft: "auto", background: "#00243d", color: "#fff",
              border: "none", borderRadius: "6px", padding: "10px 20px",
              fontSize: "0.9rem", fontWeight: 500, cursor: "pointer",
            }}
          >
            + New Submission
          </button>
        </div>

        {/* Submission History */}
        <div style={{ marginBottom: "12px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 600, color: "#141414", margin: 0 }}>
            Submission History
          </h2>
          <span style={{ fontSize: "0.82rem", color: "#6b7280" }}>
            {submissionHistory.length} submission{submissionHistory.length !== 1 ? "s" : ""} this session
          </span>
        </div>

        {submissionHistory.length === 0 ? (
          <div style={{
            background: "#fff", borderRadius: "12px", padding: "48px",
            textAlign: "center", color: "#9ca3af", fontSize: "0.9rem",
            boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
          }}>
            No submissions yet this session.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {submissionHistory.map((entry, i) => {
              const isExpanded = expandedCaseId === entry.caseId;
              const details = caseDetailsCache[entry.caseId];
              const verifyResult = verificationResults[entry.caseId];
              const isVerifying = verifyingCaseId === entry.caseId;

              return (
                <div key={i} style={{
                  background: "#fff", borderRadius: "12px",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden",
                }}>
                  {/* Card Header */}
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                      <span style={{
                        background: "#dbeafe", color: "#1e40af", borderRadius: "20px",
                        padding: "3px 12px", fontSize: "0.78rem", fontWeight: 600,
                      }}>
                        Case ID: {entry.caseId}
                      </span>
                      <span style={{
                        background: "#d1fae5", color: "#065f46", borderRadius: "20px",
                        padding: "3px 10px", fontSize: "0.78rem", fontWeight: 500,
                      }}>
                        Completed
                      </span>
                      {verifyResult && verifyPill(verifyResult)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
                      <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                        {formatDate(entry.submittedAt)}
                      </span>
                      {!verifyOnConfirm && (
                        <button
                          onClick={() => handleVerify(entry)}
                          disabled={isVerifying}
                          style={{
                            background: isVerifying ? "#6b7280" : "#00243d", color: "#fff",
                            border: "none", borderRadius: "6px", padding: "6px 14px",
                            fontSize: "0.78rem", fontWeight: 500, cursor: isVerifying ? "not-allowed" : "pointer",
                          }}
                        >
                          {isVerifying ? "Verifying…" : "Verify"}
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleExpand(entry.caseId)}
                        style={{
                          background: "none", border: "1px solid #e5e7eb", borderRadius: "6px",
                          padding: "6px 12px", fontSize: "0.78rem", color: "#374151",
                          cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
                        }}
                      >
                        {isExpanded ? "▲ Hide Details" : "▼ View Details"}
                      </button>
                    </div>
                  </div>

                  {/* Files List */}
                  <div style={{ padding: "12px 20px", display: "flex", flexDirection: "column", gap: "8px" }}>
                    {entry.files.map((f, fi) => (
                      <div key={fi} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 12px", background: "#f9fafb", borderRadius: "8px",
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <div style={{
                            width: "28px", height: "28px", borderRadius: "6px",
                            background: "#e0f2fe", display: "flex", alignItems: "center",
                            justifyContent: "center", flexShrink: 0,
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                              <polyline points="14 2 14 8 20 8"/>
                            </svg>
                          </div>
                          <span style={{ fontSize: "0.85rem", color: "#141414", fontWeight: 500 }}>
                            {f.fileName}
                          </span>
                        </div>
                        <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                          {f.blNumber && (
                            <span style={{
                              background: "#f3f4f6", color: "#374151", borderRadius: "4px",
                              padding: "2px 8px", fontSize: "0.75rem",
                            }}>
                              BL: {f.blNumber}
                            </span>
                          )}
                          {f.invoiceNumber && (
                            <span style={{
                              background: "#f3f4f6", color: "#374151", borderRadius: "4px",
                              padding: "2px 8px", fontSize: "0.75rem",
                            }}>
                              INV: {f.invoiceNumber}
                            </span>
                          )}
                          {f.type && (
                            <span style={{
                              background: "#fef3c7", color: "#92400e", borderRadius: "4px",
                              padding: "2px 8px", fontSize: "0.75rem",
                            }}>
                              {f.type}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Expanded: Shipment & Invoice Tables */}
                  {isExpanded && (
                    <div style={{ padding: "0 20px 20px" }}>
                      {details?.status === "loading" && (
                        <div style={{ padding: "24px", textAlign: "center", color: "#6b7280", fontSize: "0.85rem" }}>
                          Loading details…
                        </div>
                      )}
                      {details?.status === "error" && (
                        <div style={{ padding: "24px", textAlign: "center", color: "#dc2626", fontSize: "0.85rem" }}>
                          Failed to load details.
                        </div>
                      )}
                      {(details?.status === "done" || details?.status === "empty") && (
                        <>
                          {/* Extracted Details — per file */}
                          {entry.files.some((f) => f.metadata && Object.keys(f.metadata).length > 0) && (
                            <div style={{ marginBottom: "20px" }}>
                              <div style={{
                                fontSize: "0.88rem", fontWeight: 600, color: "#141414",
                                marginBottom: "8px", marginTop: "4px",
                              }}>
                                Extracted Details — Case {entry.caseId}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                {entry.files.map((f, fi) => {
                                  if (!f.metadata || Object.keys(f.metadata).length === 0) return null;
                                  const dropKey = `${entry.caseId}-${fi}`;
                                  const isOpen = !!openExtractedDropdowns[dropKey];
                                  const changedKeys = new Set(Object.keys(f.changed_values || {}));
                                  return (
                                    <div key={fi} style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                                      {/* File row header */}
                                      <div style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        padding: "9px 14px", background: "#f9fafb", cursor: "pointer",
                                      }}
                                        onClick={() => toggleExtracted(dropKey)}
                                      >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                            <polyline points="14 2 14 8 20 8"/>
                                          </svg>
                                          <span style={{ fontSize: "0.83rem", fontWeight: 500, color: "#141414" }}>{f.fileName}</span>
                                          {changedKeys.size > 0 && (
                                            <span style={{
                                              background: "#fef3c7", color: "#92400e", borderRadius: "4px",
                                              padding: "1px 7px", fontSize: "0.72rem", fontWeight: 500,
                                            }}>
                                              {changedKeys.size} edited
                                            </span>
                                          )}
                                        </div>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5"
                                          style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
                                          <polyline points="6 9 12 15 18 9"/>
                                        </svg>
                                      </div>
                                      {/* Extracted fields grid */}
                                      {isOpen && (
                                        <div style={{
                                          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                                          gap: "1px", background: "#e5e7eb",
                                        }}>
                                          {Object.entries(f.metadata).map(([key, value]) => {
                                            const isEdited = changedKeys.has(key);
                                            const originalVal = isEdited
                                              ? (f.metadata[key] != null ? String(f.metadata[key]) : "—")
                                              : null;
                                            const editedVal = isEdited
                                              ? (f.changed_values[key] != null ? String(f.changed_values[key]) : "—")
                                              : null;
                                            return (
                                              <div key={key} style={{ background: "#fff", padding: "10px 14px" }}>
                                                <div style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: "3px" }}>
                                                  {formatLabel(key)}
                                                </div>
                                                {isEdited ? (
                                                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                                    <span style={{ fontSize: "0.82rem", color: "#9ca3af", textDecoration: "line-through" }}>{originalVal}</span>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                                      <span style={{ fontSize: "0.85rem", color: "#111827", fontWeight: 600 }}>{editedVal}</span>
                                                      <span style={{
                                                        background: "#fef3c7", color: "#92400e", borderRadius: "4px",
                                                        padding: "1px 5px", fontSize: "0.68rem", fontWeight: 600,
                                                      }}>edited</span>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <span style={{ fontSize: "0.85rem", color: "#111827", fontWeight: 500 }}>
                                                    {value != null ? String(value) : "—"}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Invoice Details Table */}
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{
                              fontSize: "0.88rem", fontWeight: 600, color: "#141414",
                              marginBottom: "8px", marginTop: "4px",
                            }}>
                              Invoice Details — Case {entry.caseId}
                            </div>
                            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "220px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1200px" }}>
                                <thead>
                                  <tr>
                                    {["Invoice Number", "Invoice Date", "Modified Date", "UBLI", "Customer CMD Code", "BP Number", "Invoice Type", "Invoiced Amount", "Open Amount", "Currency", "Payment Term", "API Status"].map((h) => (
                                      <th key={h} style={TABLE_HEADER}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.invoices.length === 0 ? (
                                    <tr>
                                      <td colSpan={12} style={{ ...TABLE_CELL, color: "#9ca3af", textAlign: "center" }}>
                                        No invoice data
                                      </td>
                                    </tr>
                                  ) : details.invoices.map((inv, ii) => (
                                    <tr key={ii}>
                                      <td style={TABLE_CELL}>{inv.invoicenumber || "—"}</td>
                                      <td style={TABLE_CELL}>{formatDateShort(inv.invoicedate)}</td>
                                      <td style={TABLE_CELL}>{formatDateShort(inv.modifieddate)}</td>
                                      <td style={TABLE_CELL}>{inv.ubli || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.customercmdcode || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.bpnumber || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.invoicetype || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.invoicedamount != null ? inv.invoicedamount.toLocaleString() : "—"}</td>
                                      <td style={TABLE_CELL}>{inv.openamount != null ? inv.openamount.toLocaleString() : "—"}</td>
                                      <td style={TABLE_CELL}>{inv.invoicecurrency || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.paymentterm || "—"}</td>
                                      <td style={TABLE_CELL}>{inv.apistatus || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Shipment Details Table */}
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{
                              fontSize: "0.88rem", fontWeight: 600, color: "#141414",
                              marginBottom: "8px",
                            }}>
                              Shipment Details — Case {entry.caseId}
                            </div>
                            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "220px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                                <thead>
                                  <tr>
                                    {["BL Number", "Payee SCVID", "Collection Business Unit", "Payee CMD Code", "API Status", "Payment Term", "POR Country", "POD Country"].map((h) => (
                                      <th key={h} style={TABLE_HEADER}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.shipments.length === 0 ? (
                                    <tr>
                                      <td colSpan={8} style={{ ...TABLE_CELL, color: "#9ca3af", textAlign: "center" }}>
                                        No shipment data
                                      </td>
                                    </tr>
                                  ) : details.shipments.map((sh, si) => (
                                    <tr key={si}>
                                      <td style={TABLE_CELL}>{sh.blnumber || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.payeescvcid || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.collectionbusinessunit || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.payeecmdcode || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.apistatus || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.paymentterm || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.porcountry || "—"}</td>
                                      <td style={TABLE_CELL}>{sh.podcountry || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Task Details Table */}
                          <div style={{ marginBottom: "20px" }}>
                            <div style={{
                              fontSize: "0.88rem", fontWeight: 600, color: "#141414",
                              marginBottom: "8px",
                            }}>
                              Task Details — Case {entry.caseId}
                            </div>
                            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "220px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
                                <thead>
                                  <tr>
                                    {["Case ID", "BL Number", "Task Name", "Task Status", "Transport Stage", "Task Code"].map((h) => (
                                      <th key={h} style={TABLE_HEADER}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.tasks.length === 0 ? (
                                    <tr>
                                      <td colSpan={6} style={{ ...TABLE_CELL, color: "#9ca3af", textAlign: "center" }}>
                                        No task data
                                      </td>
                                    </tr>
                                  ) : details.tasks.map((t, ti) => (
                                    <tr key={ti}>
                                      <td style={TABLE_CELL}>{t.caseid || "—"}</td>
                                      <td style={TABLE_CELL}>{t.blnumber || "—"}</td>
                                      <td style={TABLE_CELL}>{t.taskname || "—"}</td>
                                      <td style={TABLE_CELL}>{t.taskstatus || "—"}</td>
                                      <td style={TABLE_CELL}>{t.transportstage || "—"}</td>
                                      <td style={TABLE_CELL}>{t.taskcode || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Validation Results Table */}
                          <div>
                            <div style={{
                              fontSize: "0.88rem", fontWeight: 600, color: "#141414",
                              marginBottom: "8px",
                            }}>
                              Validation Results — Case {entry.caseId}
                            </div>
                            <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "220px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
                                <thead>
                                  <tr>
                                    {["Rule Name", "Outcome", "Reason", "AFR Data", "Booking Data"].map((h) => (
                                      <th key={h} style={TABLE_HEADER}>{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {(details.validation_results || []).length === 0 ? (
                                    <tr>
                                      <td colSpan={5} style={{ ...TABLE_CELL, color: "#9ca3af", textAlign: "center" }}>
                                        No validation results — run verify first
                                      </td>
                                    </tr>
                                  ) : (details.validation_results || []).map((vr, vi) => {
                                    const outcomeCfg = {
                                      Pass:      { bg: "#d1fae5", color: "#065f46" },
                                      Fail:      { bg: "#fee2e2", color: "#991b1b" },
                                      Exception: { bg: "#fef3c7", color: "#92400e" },
                                    }[vr.outcome] || { bg: "#f3f4f6", color: "#374151" };
                                    return (
                                      <tr key={vi}>
                                        <td style={{ ...TABLE_CELL, fontWeight: 500 }}>{vr.rulename || "—"}</td>
                                        <td style={TABLE_CELL}>
                                          <span style={{
                                            background: outcomeCfg.bg, color: outcomeCfg.color,
                                            borderRadius: "20px", padding: "2px 10px",
                                            fontSize: "0.75rem", fontWeight: 500,
                                          }}>
                                            {vr.outcome || "—"}
                                          </span>
                                        </td>
                                        <td style={{ ...TABLE_CELL, maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }}>{vr.reason || "—"}</td>
                                        <td style={TABLE_CELL}>{vr.afr_data || "—"}</td>
                                        <td style={TABLE_CELL}>{vr.booking_data || "—"}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
